import { ResponsiveLine } from "@nivo/line";
import { useTranslation } from "react-i18next";

import { convertValueToLocale } from "helpers/convertValueToLocale";

import { Typography } from "shared/ui/components/Typography";

import styles from "./CashFlowChart.module.scss";

const CustomTooltip = ({ point }: { point: any }) => {
  const { data } = point;

  return (
    <div className={styles.tooltip}>
      <Typography variant="bodyS" weight="semiBold">
        {data.xFormatted}:
      </Typography>

      <div className={styles.tooltipContent}>
        <Typography variant="bodyS">Ihre Kalkulation:</Typography>
        <Typography variant="bodyS">
          {convertValueToLocale(data.cashFlowPrimary)}
        </Typography>
      </div>

      <div className={styles.tooltipContent}>
        <Typography variant="bodyS">
          {data.alternativePrefix} Fördermaßnahmen:
        </Typography>
        <Typography variant="bodyS">
          {convertValueToLocale(data.cashFlowAlternative)}
        </Typography>
      </div>

      {data.yearIndex > 10 && (
        <>
          <Typography
            className={styles.tooltipTitle}
            variant="bodyS"
            weight="semiBold"
          >
            nach {data.yearIndex} Jahren:
          </Typography>

          <div className={styles.tooltipContent}>
            <Typography variant="bodyS">Gesamtinvestition:</Typography>
            <Typography variant="bodyS">
              {convertValueToLocale(data.investmentAcc)}
            </Typography>
          </div>

          <div className={styles.tooltipContent}>
            <Typography variant="bodyS">Nettogewinn bei Verkauf:</Typography>
            <Typography variant="bodyS">
              {convertValueToLocale(data.earlySaleProfit)}
            </Typography>
          </div>
        </>
      )}
    </div>
  );
};

const CashFlowChart = ({
  chartData,
  yearsToShow,
}: {
  chartData: any;
  yearsToShow: number[];
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.chart}>
      <Typography variant="h5" weight="semiBold">
        {t("calculator_pro_page.analytics.cash_flow_chart.title")}
      </Typography>

      <div className={styles.legend}>
        {chartData.legend.map((legendItem: any) => (
          <div className={styles.legendItem} key={legendItem.label}>
            <div
              className={styles.legendItemIcon}
              style={{ backgroundColor: legendItem.color }}
            />
            <Typography variant="bodyS">{legendItem.label}</Typography>
          </div>
        ))}
      </div>

      <div className={styles.inner}>
        <ResponsiveLine
          data={chartData.charts}
          margin={{ top: 10, right: 12, bottom: 25, left: 60 }}
          yScale={{
            type: "linear",
            min: "auto",
            max: "auto",
            stacked: false,
            reverse: false,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 5,
            tickRotation: 0,
            format: (value: number) => convertValueToLocale(value),
          }}
          axisBottom={{
            format: (year: number) => {
              return yearsToShow.includes(year) ? year : "";
            },
          }}
          enablePoints={false}
          pointSize={10}
          colors={chartData.colors}
          pointColor={{ theme: "background" }}
          pointBorderWidth={2}
          pointBorderColor={{ from: "seriesColor" }}
          pointLabelYOffset={-12}
          enableTouchCrosshair={true}
          useMesh={true}
          tooltip={CustomTooltip}
        />
      </div>
    </div>
  );
};

export default CashFlowChart;
